import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "~/api/provider";
import type {
  ReasoningMemoryGraph,
  ReasoningSession,
  ReasoningSourceBarrier,
  ReasoningTrace,
} from "~/api/reasoningTypes";

/** One trace page; the poll, the first load and "Load more" all ask for the same size. */
export const TRACE_PAGE_SIZE = 100;
export const SESSION_POLL_MS = 3000;
/** After two minutes on one run the poll slows to this. */
export const SESSION_POLL_SLOW_MS = 15000;
export const SESSION_POLL_BACKOFF_AFTER_MS = 2 * 60 * 1000;

const message = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Events are append-only; a later page only adds. Duplicates (an overlapping cursor) collapse by id. */
export function mergeTrace(prior: ReasoningTrace | null, page: ReasoningTrace): ReasoningTrace {
  if (!prior) return page;
  const byId = new Map(prior.trace.events.map((event) => [event.id, event]));
  for (const event of page.trace.events) byId.set(event.id, event);
  return {
    ...page,
    trace: {
      ...page.trace,
      events: [...byId.values()].sort((a, b) => a.sequence - b.sequence),
      // A page that answered "no more" can still leave earlier holes; the newest answer wins.
      nextSequence: page.trace.nextSequence,
    },
  };
}

/**
 * The one owner of a run's reasoning record: initial load, refresh after a command, the poll
 * (session → trace → graph) and the merge. The poll runs every 3 s, slows to 15 s after two
 * minutes, and stops for good once the run is terminal AND its session has an acceptance —
 * nothing the server holds will change after that. The Neo4j projection is read only when the
 * trace's high-watermark moved, so a quiet session costs one round trip per tick, not three.
 */
export function useReasoningSession({
  runId,
  sessionId,
  terminal,
}: {
  runId: string;
  sessionId?: string;
  /** The run has reached a final status (completed / failed / cancelled). */
  terminal: boolean;
}) {
  const api = useApi();
  const [session, setSession] = useState<ReasoningSession | null>(null);
  const [sourceBarriers, setSourceBarriers] = useState<ReasoningSourceBarrier[]>([]);
  const [trace, setTrace] = useState<ReasoningTrace | null>(null);
  const [memoryGraph, setMemoryGraph] = useState<ReasoningMemoryGraph | null>(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const cursor = useRef(0);
  const graphWatermark = useRef(-1);
  const traceRef = useRef<ReasoningTrace | null>(null);
  const generation = useRef(0);
  const startedAt = useRef(Date.now());

  const load = useCallback(
    async (mode: "full" | "incremental", epoch: number) => {
      const found = await (sessionId ? api.reasoning.get(sessionId) : api.reasoning.byRun(runId));
      if (epoch !== generation.current) return;
      setSession(found.session);
      setSourceBarriers(found.sourceBarriers ?? []);
      if (!found.session) {
        traceRef.current = null;
        cursor.current = 0;
        graphWatermark.current = -1;
        setTrace(null);
        setMemoryGraph(null);
        return;
      }
      const after = mode === "full" ? 0 : cursor.current;
      const page = await api.reasoning.trace(found.session.id, after, TRACE_PAGE_SIZE);
      if (epoch !== generation.current) return;
      const merged = mode === "full" ? page : mergeTrace(traceRef.current, page);
      cursor.current = merged.trace.events.at(-1)?.sequence ?? cursor.current;
      traceRef.current = merged;
      setTrace(merged);
      const watermark = page.traceManifest.eventSequenceHighWatermark;
      if (mode === "full" || watermark !== graphWatermark.current) {
        const graph = await api.reasoning.graph(found.session.id).catch(() => null);
        if (epoch !== generation.current) return;
        graphWatermark.current = watermark;
        if (graph) setMemoryGraph(graph);
      }
    },
    [api, runId, sessionId],
  );

  useEffect(() => {
    generation.current += 1;
    const epoch = generation.current;
    traceRef.current = null;
    cursor.current = 0;
    graphWatermark.current = -1;
    startedAt.current = Date.now();
    setSession(null);
    setSourceBarriers([]);
    setTrace(null);
    setMemoryGraph(null);
    setError("");
    setLoaded(false);
    load("full", epoch)
      .catch((cause: unknown) => {
        if (epoch === generation.current) setError(message(cause));
      })
      .finally(() => {
        if (epoch === generation.current) setLoaded(true);
      });
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const stopped = terminal && !!session?.currentAcceptanceRecordId;
  useEffect(() => {
    if (!loaded || stopped) return;
    const epoch = generation.current;
    let live = true;
    const delay = () =>
      Date.now() - startedAt.current > SESSION_POLL_BACKOFF_AFTER_MS
        ? SESSION_POLL_SLOW_MS
        : SESSION_POLL_MS;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      if (!live) return;
      try {
        await load("incremental", epoch);
      } catch {
        /* The visible record stays available during a transient refresh error. */
      }
      if (live) timer = setTimeout(tick, delay());
    };
    timer = setTimeout(tick, delay());
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [load, loaded, stopped]);

  /** After a command: the session and the new events, without resetting what is on screen. */
  const refresh = useCallback(() => load("incremental", generation.current), [load]);

  const loadMore = useCallback(async () => {
    const current = traceRef.current;
    const next = current?.trace.nextSequence;
    if (!session || current === null || next === null || next === undefined) return;
    const epoch = generation.current;
    const page = await api.reasoning.trace(session.id, next, TRACE_PAGE_SIZE);
    if (epoch !== generation.current) return;
    const merged = mergeTrace(traceRef.current, page);
    cursor.current = Math.max(cursor.current, merged.trace.events.at(-1)?.sequence ?? 0);
    traceRef.current = merged;
    setTrace(merged);
  }, [api, session]);

  /** A child that committed a command hands back the session it read; the trace follows on refresh. */
  const replaceSession = useCallback((next: ReasoningSession) => setSession(next), []);

  return {
    session,
    sourceBarriers,
    trace,
    memoryGraph,
    error,
    loaded,
    polling: loaded && !stopped,
    refresh,
    loadMore,
    replaceSession,
    setError,
  };
}
